import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import {
  CircularProgress,
  styled,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Link,
  Button,
  useMediaQuery,
  Box,
} from "@mui/material";
import { getApiData } from "../../../../utils/APIHelper";
import styles from "../../styles";
import moment from "moment";
import { paymentStatusStyleArray } from "../../config";
import { formattedAmountWithoutAed } from "../../../../utils/CommonFunction";
import ProjectStats from "./ProjectStats";
import { ReactComponent as PdfIcon } from "../../../../assets/svg/SmallPdfIcon.svg";
import { ReactComponent as SmallDhiramIcon } from "../../../../assets/svg/SmallDhiramIcon.svg";
import Permission from "../../../../components/Permission";
import { renderCurrencyIcon } from "../../../../utils/CurrencyUtils";
import { toast } from "react-toastify";

import MobileView from "./MobileView";

export const getStatusDisplayText = (text) => {
  if (typeof text !== "string") return text;

  if (text.toLowerCase() === "created") {
    return "-";
  }

  const displayText = text
    .replace(/[-_]/g, " ")
    .toLowerCase()
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
  return displayText;
};

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  borderBottom: "none",
  textTransform: "capitalize",
}));

export default function ContractorPayments({ projectId, isMobile, styles }) {
  const { userData } = useSelector((state) => state.auth);
  const [paymentDetails, setPaymentDetails] = useState([]);
  const [loading, setLoading] = useState(false);

  const isMobileDevice = isMobile;
  const componentStyles = styles || styles(isMobileDevice);

  useEffect(() => {
    if (userData?.contractor_data?.id && projectId) {
      fetchData();
    }
  }, [userData?.contractor_data?.id, projectId]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const response = await getApiData(
        `api/v2/contractors/${userData.contractor_data.id}/balanceSheet?projectId=${projectId}`,
        "GET",
        {},
        {},
        true
      );
      setPaymentDetails(response?.projectBalanceSheet || []);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching payment details:", error);
    } finally {
      setLoading(false);
    }
  };

  const MAX_FILE_SIZE_MB = 50;

  const handleFileSelect = async (e, payoutId) => {
    const file = e.target.files[0];

    if (!file) {
      toast.error("No file selected");
      return;
    }

    if (file.type !== "application/pdf") {
      toast.error("Only PDF files are allowed");
      return;
    }

    const fileSizeMB = file.size / (1024 * 1024);
    if (fileSizeMB > MAX_FILE_SIZE_MB) {
      toast.error("File size should not exceed 50MB");
      return;
    }

    await handleStep1(file, payoutId);
  };

  const handleStep1 = async (file, payoutId) => {
    try {
      const toastId = toast.loading("Preparing upload...");

      const response = await getApiData(
        `api/v2/blob/sas/upload`,
        "POST",
        {
          entityType: "contractorPayout",
          entityId: payoutId,
          fileName: file.name,
        },
        { "Content-Type": "application/json" },
        true
      );

      if (response) {
        toast.update(toastId, {
          render: "Upload link generated",
          type: "success",
          isLoading: false,
          autoClose: 1000,
        });
        await uploadToBlob(file, response);
      } else {
        toast.update(toastId, {
          render: "Failed to get upload URL",
          type: "error",
          isLoading: false,
          autoClose: 3000,
        });
      }
    } catch (error) {
      toast.update("prepareUpload", {
        render: "Error generating upload link",
        type: "error",
        isLoading: false,
        autoClose: 3000,
      });
      console.error(error);
    }
  };

  const uploadToBlob = async (file, sasUrl) => {
    const toastId = toast.loading("Uploading PDF...");
    try {
      const url = new URL(sasUrl);
      const fileId = url.searchParams.get("id");

      await fetch(sasUrl, {
        method: "PUT",
        headers: {
          "x-ms-blob-type": "BlockBlob",
          "Content-Type": file.type || "application/octet-stream",
        },
        body: file,
      });

      toast.update(toastId, {
        render: "PDF uploaded to blob",
        type: "success",
        isLoading: false,
        autoClose: 1000,
      });

      await sendFileUrlToServer(fileId);
    } catch (error) {
      toast.update(toastId, {
        render: "Upload to blob failed",
        type: "error",
        isLoading: false,
        autoClose: 3000,
      });
      console.error(error);
    }
  };

  const sendFileUrlToServer = async (fileId) => {
    const toastId = toast.loading("Saving invoice...");
    try {
      const confirmResponse = await getApiData(
        `api/v2/blob/confirm/upload/${fileId}`,
        "PUT",
        {},
        { "Content-Type": "application/json" },
        true
      );

      if (confirmResponse && confirmResponse.entityId) {
        toast.update(toastId, {
          render: "PDF uploaded successfully",
          type: "success",
          isLoading: false,
          autoClose: 3000,
        });

        const payoutIdToUpdate = confirmResponse.entityId;
        const newUrl = confirmResponse.blobUrl;
        const newBlobFileId = confirmResponse.id;

        const newInvoiceData = {
          url: newUrl,
          blobFileId: newBlobFileId,
          approve: false,
        };
        const newInvoiceUrlString = JSON.stringify(newInvoiceData);

        setPaymentDetails((prevDetails) => {
          const newDetails = JSON.parse(JSON.stringify(prevDetails));
          const project = newDetails[0];

          if (project && project.payouts) {
            const payoutToUpdate = project.payouts.find(
              (p) => p.id === payoutIdToUpdate
            );
            if (payoutToUpdate) {
              payoutToUpdate.invoiceUrl = newInvoiceUrlString;
            }
          }
          return newDetails;
        });
      } else {
        toast.update(toastId, {
          render: "Failed to save invoice",
          type: "error",
          isLoading: false,
          autoClose: 3000,
        });
      }
    } catch (error) {
      toast.update(toastId, {
        render: "Error saving invoice",
        type: "error",
        isLoading: false,
        autoClose: 3000,
      });
      console.error(error);
    }
  };

  const handleDeleteFile = async (fileId, payoutId) => {
    try {
      const toastId = toast.loading("Deleting file...");

      const response = await getApiData(
        `api/v2/blob/${fileId}`,
        "DELETE",
        {},
        { "Content-Type": "application/json" },
        true
      );

      if (response) {
        toast.update(toastId, {
          render: "File deleted successfully",
          type: "success",
          isLoading: false,
          autoClose: 3000,
        });

        setPaymentDetails((prevDetails) => {
          const newDetails = JSON.parse(JSON.stringify(prevDetails));
          const project = newDetails[0];

          if (project && project.payouts) {
            const payoutToUpdate = project.payouts.find(
              (p) => p.id === payoutId
            );

            if (payoutToUpdate) {
              payoutToUpdate.invoiceUrl = null;
              payoutToUpdate.invoice = null;
            }
          }
          return newDetails;
        });
      } else {
        toast.update(toastId, {
          render: "Failed to delete file",
          type: "error",
          isLoading: false,
          autoClose: 3000,
        });
      }
    } catch (error) {
      toast.error("Error deleting file");
      console.error(error);
    }
  };

  return loading ? (
    <center>
      <CircularProgress />
    </center>
  ) : (
    <>
      <ProjectStats summary={paymentDetails?.[0]} isMobile={isMobileDevice} />
      {isMobileDevice ? (
        <MobileView
          details={paymentDetails}
          handleDeleteFile={handleDeleteFile}
          handleFileSelect={handleFileSelect}
        />
      ) : (
        <TableContainer
          component={Paper}
          sx={{
            width: "100%",
            overflow: "visible",
            boxShadow: "none",
            marginTop: "2vh",
          }}
        >
          <Table
            sx={{
              "& thead": {
                "& th": {
                  color: "#949DAA",
                  fontWeight: "500",
                },
              },
              "& tbody": {
                "& tr:hover": {
                  backgroundColor: "#f7f7f7",
                },
                "& tr": {
                  borderBottom: "none",
                },
              },
            }}
          >
            <TableHead>
              <TableRow>
                <StyledTableCell>Payment</StyledTableCell>
                <StyledTableCell>Due Date</StyledTableCell>
                <StyledTableCell>Settled On</StyledTableCell>
                <StyledTableCell sx={{ flex: 0.5, textAlign: "right" }}>
                  <strong
                    style={{
                      display: "flex",
                      flexDirection: "row",
                      alignItems: "center",
                      justifyContent: "flex-end",
                    }}
                  >
                    Amount Due ({renderCurrencyIcon('small')})
                  </strong>
                </StyledTableCell>
                <StyledTableCell>Status</StyledTableCell>
                <StyledTableCell>Paymnet Receipt</StyledTableCell>
                <StyledTableCell>Invoice</StyledTableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paymentDetails?.[0]?.payouts?.map((item, idx) => {
                const metaData = item?.metadata
                  ? JSON.parse(item?.metadata)
                  : null;

                const invoiceData = item?.invoice
                  ? item.invoice
                  : item?.invoiceUrl
                    ? JSON.parse(item.invoiceUrl)
                    : null;

                let invoiceStatus = null;
                if (invoiceData) {
                  if (invoiceData.status) {
                    invoiceStatus = invoiceData.status;
                  } else {
                    invoiceStatus = invoiceData.approve
                      ? "APPROVED"
                      : "IN_REVIEW";
                  }
                }

                const fileUrl = metaData?.file?.fileUrl;
                return (
                  <>
                    <TableRow key={item.id}>
                      <StyledTableCell>{item?.groupName}</StyledTableCell>

                      <StyledTableCell>
                        <p style={styles.greyRow}>
                          {item?.dueDate
                            ? moment(item?.dueDate).format("MMM DD, YYYY")
                            : "-"}
                        </p>
                      </StyledTableCell>

                      <StyledTableCell>
                        <p style={styles.greyRow}>
                          {item?.releaseDate
                            ? moment(item?.releaseDate).format("MMM DD, YYYY")
                            : "-"}
                        </p>
                      </StyledTableCell>

                      <StyledTableCell sx={{ flex: 0.5, textAlign: "right" }}>
                        {`${formattedAmountWithoutAed(item?.amount)}`}
                      </StyledTableCell>

                      <StyledTableCell>
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "row",
                            alignItems: "center",
                            gap: "10px",
                          }}
                        >
                          <Box
                            sx={{
                              width: "15px",
                              height: "15px",
                              borderRadius: "50%",
                              border: "3px solid rgb(229, 229, 229)",
                              backgroundColor:
                                paymentStatusStyleArray.find(
                                  (style) => style.status === item?.status
                                )?.backgroundColor || "#FFAD5C",
                            }}
                          />
                          <Box
                            sx={{
                              fontSize: "14px",
                              color: "#0D0D0E",
                            }}
                          >
                            {getStatusDisplayText(item?.status)}
                          </Box>
                        </Box>
                      </StyledTableCell>

                      <StyledTableCell>
                        {fileUrl && (
                          <Link
                            href={fileUrl}
                            target="_blank"
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              textDecoration: "none",
                            }}
                          >
                            <PdfIcon />
                          </Link>
                        )}
                      </StyledTableCell>

                      <StyledTableCell>
                        {item.amount !== 0 && (
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "10px",
                            }}
                          >
                            {!invoiceData?.url ? (
                              item.status === "DUE" && (
                                <Permission resource="INVOICES" action="CREATE">
                                  <Button
                                    variant="contained"
                                    component="label"
                                    sx={{
                                      backgroundColor: "#4D6BF6",
                                      borderRadius: "12px",
                                      fontSize: "14px",
                                      fontWeight: 500,
                                      boxShadow: "none",
                                      color: "#fff",
                                      border: "none",
                                      padding: ".7vh 1vw",
                                      cursor: "pointer",
                                      fontFamily: "Inter Tight",
                                      whiteSpace: "nowrap",
                                      "&:hover": {
                                        backgroundColor: "#4D6BF6",
                                        border: "none",
                                      },
                                    }}
                                  >
                                    Upload Invoice
                                    <input
                                      id={`pdf-upload-${item.id}`}
                                      type="file"
                                      accept="application/pdf"
                                      style={{ display: "none" }}
                                      onChange={(e) =>
                                        handleFileSelect(e, item.id)
                                      }
                                    />
                                  </Button>
                                </Permission>
                              )
                            ) : (
                              <>
                                <Link
                                  href={invoiceData.url}
                                  target="_blank"
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    textDecoration: "none",
                                  }}
                                  title="Download Invoice"
                                >
                                  <PdfIcon />
                                </Link>

                                {invoiceStatus === "IN_REVIEW" && (
                                  <Box
                                    sx={{
                                      color: "#E6A23C",
                                      fontWeight: 500,
                                      fontSize: "14px",
                                    }}
                                  >
                                    In Review
                                  </Box>
                                )}

                                {invoiceStatus === "APPROVED" && (
                                  <Box
                                    sx={{
                                      color: "#28a745",
                                      fontWeight: 500,
                                      fontSize: "14px",
                                    }}
                                  >
                                    Approved
                                  </Box>
                                )}

                                {invoiceStatus === "REJECTED" && (
                                  <>
                                    <Box
                                      sx={{
                                        color: "#D64B64",
                                        fontWeight: 500,
                                        fontSize: "14px",
                                      }}
                                    >
                                      Rejected
                                    </Box>
                                    <Button
                                      variant="outlined"
                                      onClick={() =>
                                        handleDeleteFile(
                                          invoiceData.blobFileId,
                                          item.id
                                        )
                                      }
                                      sx={{
                                        backgroundColor: "#fff",
                                        borderRadius: "8px",
                                        fontSize: "14px",
                                        fontWeight: 500,
                                        boxShadow: "none",
                                        color: "#D64B64",
                                        border: "1px solid #D7D8D9",
                                        padding: ".5vh .8vw",
                                        cursor: "pointer",
                                        fontFamily: "Inter Tight",
                                        whiteSpace: "nowrap",
                                        minWidth: 0,
                                        "&:hover": {
                                          backgroundColor: "#fff",
                                          border: "1px solid #D7D8D9",
                                        },
                                      }}
                                    >
                                      Delete
                                    </Button>
                                  </>
                                )}
                              </>
                            )}
                          </div>
                        )}
                      </StyledTableCell>
                    </TableRow>
                  </>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </>
  );
}
